/**
 * Sticky hard-trap state for email verification.
 *
 * When a gate redirects a sitter to /verify-email, we mark verification pending
 * in sessionStorage keyed to the current SSO account id. A global route guard
 * then keeps that same sitter on /verify-email until verified.
 *
 * Scoped to account id so logout / account switch cannot trap a different user
 * (or a parent) with a leftover flag. Mismatched or missing JWT clears the flag.
 *
 * markEmailVerificationRequired returns whether the trap was armed. Callers that
 * redirect should also re-arm on /verify-email mount as a backup (see VerifyEmailPage).
 */
import { isSafeInternalPath, isVerifyEmailPath } from '@/apps/verifyEmail/helpers/paths';
import { invalidateEmailVerificationStatus } from '@/apps/verifyEmail/helpers/emailVerificationStatus';
import { currentAccountId, sessionStorageOrNull as storage } from '@/apps/verifyEmail/helpers/sessionAccountStorage';

export const EMAIL_VERIFICATION_PENDING_KEY = 'sc_email_verification_pending';
export const EMAIL_VERIFICATION_PENDING_NEXT_KEY = 'sc_email_verification_pending_next';

/**
 * Arm the sticky trap for the current account.
 * @returns true when pending state was written; false if account id / storage unavailable.
 */
export function markEmailVerificationRequired(returnPath?: string | null): boolean {
  const session = storage();
  const id = currentAccountId();
  if (!session || !id) {
    return false;
  }

  session.setItem(EMAIL_VERIFICATION_PENDING_KEY, id);

  if (typeof returnPath === 'string' && isSafeInternalPath(returnPath) && !isVerifyEmailPath(returnPath)) {
    session.setItem(EMAIL_VERIFICATION_PENDING_NEXT_KEY, returnPath);
  }

  return true;
}

/** Call after verification succeeds (or we learn they are already verified). */
export function clearEmailVerificationRequired(): void {
  const session = storage();
  if (session) {
    session.removeItem(EMAIL_VERIFICATION_PENDING_KEY);
    session.removeItem(EMAIL_VERIFICATION_PENDING_NEXT_KEY);
  }

  invalidateEmailVerificationStatus();
}

/**
 * True only when pending is set for the currently authenticated account.
 * Clears stale flags left by a previous user in this tab.
 */
export function isEmailVerificationPending(): boolean {
  const session = storage();
  const storedId = session?.getItem(EMAIL_VERIFICATION_PENDING_KEY);
  if (!storedId) {
    return false;
  }

  const id = currentAccountId();
  if (!id || storedId !== id) {
    clearEmailVerificationRequired();
    return false;
  }

  return true;
}

/** Original destination captured when the trap was first armed. */
export function getPendingEmailVerificationReturnPath(): string | null {
  const session = storage();
  const value = session?.getItem(EMAIL_VERIFICATION_PENDING_NEXT_KEY);
  if (!value || !isSafeInternalPath(value) || isVerifyEmailPath(value)) {
    return null;
  }
  return value;
}
